/**
 * Le temps d'un récital, mis en mots.
 *
 * # Pourquoi un module pur
 *
 * Le compte à rebours de la salle d'attente, la date relative des cartes de
 * `/live` et la date en toutes lettres sous le titre lisent la même horloge.
 * Les écrire dans chaque composant, c'est trois façons de compter les jours
 * qui divergent au premier changement d'heure ; ici il n'y en a qu'une, et
 * elle se teste sans DOM (`__tests__/live/recital-time.test.ts`).
 *
 * # `now` est toujours reçu, jamais lu
 *
 * Aucune fonction n'appelle `Date.now()`. L'instant vient de l'appelant, ce
 * qui rend le module déterministe pour les tests et laisse le composant
 * choisir son horloge : celle du rendu serveur d'abord, puis le tic client —
 * un `Date.now()` dans le rendu ferait diverger l'hydratation.
 *
 * # En français, en heure locale, à la main
 *
 * Les libellés sont composés depuis les accesseurs locaux de `Date` plutôt
 * que par `Intl.DateTimeFormat` : ce qu'`Intl` rend dépend des données ICU
 * embarquées (Node en a moins que Chrome), et « sam. 20 sept., 19:00 » ne se
 * lit pas comme une phrase. Le fuseau reste celui de la machine : un récital
 * à 19:00 chez l'interprète s'affiche à 20:00 chez qui a une heure d'avance,
 * et c'est la seule réponse juste.
 */

/** Un instant : millisecondes Unix, ou la chaîne ISO 8601 que l'API rend. */
export type Instant = number | string;

const SECOND = 1_000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const WEEKDAYS = [
  "dimanche",
  "lundi",
  "mardi",
  "mercredi",
  "jeudi",
  "vendredi",
  "samedi",
] as const;

const MONTHS = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
] as const;

/** Ce qu'affiche le compte à rebours. Tout vaut zéro une fois l'heure passée. */
export interface CountdownParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  /** Ce qui reste, jamais négatif. */
  remainingMs: number;
  /** L'heure est passée : le compte est à zéro et « ça commence… ». */
  overdue: boolean;
}

/**
 * Le temps qui reste, découpé pour l'affichage.
 *
 * Tronqué et non arrondi : un compte à rebours qui afficherait « 1 s » alors
 * qu'il reste 1,4 s puis « 0 s » pendant 0,4 s tremblerait à la fin. Avec la
 * troncature, chaque valeur reste exactement une seconde à l'écran.
 */
export function countdownParts(now: number, at: Instant): CountdownParts {
  const target = toMs(at);

  if (Number.isNaN(target)) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, remainingMs: 0, overdue: false };
  }

  const remainingMs = Math.max(0, target - now);

  return {
    days: Math.floor(remainingMs / DAY),
    hours: Math.floor((remainingMs % DAY) / HOUR),
    minutes: Math.floor((remainingMs % HOUR) / MINUTE),
    seconds: Math.floor((remainingMs % MINUTE) / SECOND),
    remainingMs,
    overdue: target <= now,
  };
}

/**
 * « dans 12 min », « dans 2 h 30 », « aujourd'hui 19:00 », « demain 19:00 »,
 * « dans 3 jours » ; « il y a 5 min » quand l'heure est passée.
 *
 * # La distance d'abord, le calendrier ensuite
 *
 * En dessous de douze heures on compte le temps qui reste, quel que soit le
 * jour : à 23:00, un récital à 01:00 est « dans 2 h », pas « demain 01:00 »,
 * parce que c'est la distance qui décide si l'on attend ou si l'on revient.
 * Au-delà, c'est le jour qui compte — « demain 19:00 » se retient, « dans
 * 28 h » se calcule.
 */
export function relativeLabel(now: number, at: Instant): string {
  const target = toMs(at);

  if (Number.isNaN(target)) {
    return "";
  }

  const diff = target - now;

  if (Math.abs(diff) < 45 * SECOND) {
    return "maintenant";
  }

  if (diff < 0) {
    return `il y a ${distance(-diff)}`;
  }

  if (diff < 12 * HOUR) {
    return `dans ${distance(diff)}`;
  }

  const days = calendarDaysBetween(now, target);
  const clock = clockLabel(new Date(target));

  if (days <= 0) {
    return `aujourd'hui ${clock}`;
  }

  if (days === 1) {
    return `demain ${clock}`;
  }

  if (days < 14) {
    return `dans ${days} jours`;
  }

  if (days < 60) {
    return `dans ${Math.round(days / 7)} semaines`;
  }

  return `dans ${Math.round(days / 30)} mois`;
}

/**
 * « samedi 20 septembre · 19:00 ».
 *
 * L'année n'apparaît que si elle n'est pas celle de `now` : la répéter sur
 * chaque carte serait du bruit, la taire sur un récital de janvier vu en
 * décembre serait un piège.
 */
export function absoluteLabel(at: Instant, now: number): string {
  const target = toMs(at);

  if (Number.isNaN(target)) {
    return "";
  }

  const date = new Date(target);
  const day = date.getDate();
  const year =
    date.getFullYear() === new Date(now).getFullYear() ? "" : ` ${date.getFullYear()}`;

  return `${WEEKDAYS[date.getDay()]} ${day === 1 ? "1er" : day} ${MONTHS[date.getMonth()]}${year} · ${clockLabel(date)}`;
}

/**
 * « 12 min », « 1 h 14 », « 2 h » : la durée entre deux instants.
 *
 * Plancher à une minute — « depuis 0 min » se lit comme un compteur en panne,
 * là où « depuis 1 min » dit que ça vient de commencer.
 */
export function elapsedLabel(from: Instant, to: Instant): string {
  const start = toMs(from);
  const end = toMs(to);

  if (Number.isNaN(start) || Number.isNaN(end)) {
    return "";
  }

  return distance(Math.max(0, end - start));
}

/** « 12 min », « 2 h 05 », « 2 h », « 3 jours ». */
function distance(ms: number): string {
  if (ms < HOUR) {
    return `${Math.max(1, Math.floor(ms / MINUTE))} min`;
  }

  if (ms < DAY) {
    const hours = Math.floor(ms / HOUR);
    const rest = Math.floor((ms % HOUR) / MINUTE);

    return rest === 0 ? `${hours} h` : `${hours} h ${pad(rest)}`;
  }

  const days = Math.floor(ms / DAY);

  return `${days} jour${days > 1 ? "s" : ""}`;
}

/**
 * Le nombre de minuits entre deux instants, en heure locale.
 *
 * Arrondi et non tronqué : un changement d'heure entre les deux fait durer un
 * jour 23 ou 25 heures, et la division tomberait alors juste à côté.
 */
function calendarDaysBetween(from: number, to: number): number {
  return Math.round((startOfDay(to) - startOfDay(from)) / DAY);
}

function startOfDay(ms: number): number {
  return new Date(ms).setHours(0, 0, 0, 0);
}

function clockLabel(date: Date): string {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

function toMs(at: Instant): number {
  return typeof at === "number" ? at : Date.parse(at);
}
